/**
 * Data Room Checklist Screen
 *
 * Interactive data room checklist with collapsible categories,
 * required/optional sections, and domain-specific requirements.
 */

import { useState, useMemo } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import AnimatedPressable from '@/components/AnimatedPressable';
import Animated, { FadeIn, FadeInDown, useAnimatedStyle, withTiming, useSharedValue } from 'react-native-reanimated';
import {
  FileText,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Check,
  MinusCircle,
  Info,
  Shield,
  AlertTriangle,
  FolderOpen,
  Upload,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { useProfileStore } from '@/lib/state/profile-store';
import {
  useDataRoomStore,
  calculateProgress,
  getStatusColor,
  getStatusLabel,
  isDocumentRequired,
  CATEGORY_LABELS,
  STAGE_LABELS,
  DOMAIN_LABELS,
  getDomainDocuments,
  DATA_ROOM_DOCUMENTS,
  type DocumentCategory,
  type FundingStage,
  type DocumentStatus,
  type StartupDomain,
} from '@/lib/dataroom';
import { useAlert } from '@/components/AlertDialog';

const STAGE_OPTIONS: FundingStage[] = ['pre-seed', 'seed', 'series-a', 'series-b-plus', 'pre-ipo'];
const DOMAIN_OPTIONS: StartupDomain[] = ['saas', 'fintech', 'healthcare', 'edtech', 'marketplace', 'ai-ml', 'hardware', 'biotech', 'consumer', 'enterprise', 'other'];

// Collapsible Section Component
function CollapsibleSection({
  title,
  count,
  total,
  isOpen,
  onToggle,
  children,
  variant = 'category',
  colors,
}: {
  title: string;
  count: number;
  total: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  variant?: 'category' | 'required' | 'optional';
  colors: ReturnType<typeof useThemeColors>;
}) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 100;
  const isComplete = count === total;

  const getBgColor = () => {
    if (variant === 'category') return colors.surface.primary;
    if (variant === 'required') return `${colors.error}08`;
    return `${colors.info}08`;
  };

  const getAccentColor = () => {
    if (variant === 'required') return colors.error;
    if (variant === 'optional') return colors.info;
    return colors.primary[600];
  };

  return (
    <View
      className="rounded-xl overflow-hidden mb-2"
      style={{
        backgroundColor: getBgColor(),
        borderWidth: variant === 'category' ? 1 : 0,
        borderColor: colors.border.light,
      }}
    >
      <Pressable
        onPress={onToggle}
        className="flex-row items-center p-4"
        style={{ backgroundColor: variant === 'category' ? colors.surface.primary : 'transparent' }}
      >
        <View className="flex-1 flex-row items-center gap-3">
          {variant === 'category' && (
            <View
              className="w-10 h-10 rounded-xl items-center justify-center"
              style={{ backgroundColor: isComplete ? `${colors.success}15` : `${getAccentColor()}15` }}
            >
              {isComplete ? (
                <CheckCircle2 size={20} color={colors.success} />
              ) : (
                <FolderOpen size={20} color={getAccentColor()} />
              )}
            </View>
          )}
          {variant !== 'category' && (
            <View
              className="w-6 h-6 rounded-full items-center justify-center"
              style={{ backgroundColor: `${getAccentColor()}15` }}
            >
              {variant === 'required' ? (
                <AlertCircle size={14} color={getAccentColor()} />
              ) : (
                <Info size={14} color={getAccentColor()} />
              )}
            </View>
          )}
          <View className="flex-1">
            <Text
              style={{ color: colors.text.primary }}
              className={variant === 'category' ? 'font-semibold' : 'font-medium text-sm'}
            >
              {title}
            </Text>
            {variant === 'category' && (
              <View className="flex-row items-center gap-2 mt-1">
                <View className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: colors.surface.secondary, maxWidth: 100 }}>
                  <View
                    className="h-full rounded-full"
                    style={{
                      backgroundColor: isComplete ? colors.success : percentage >= 50 ? colors.warning : colors.error,
                      width: `${percentage}%`,
                    }}
                  />
                </View>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  {percentage}%
                </Text>
              </View>
            )}
          </View>
        </View>
        <View className="flex-row items-center gap-2">
          <View
            className="px-2 py-1 rounded-md"
            style={{ backgroundColor: isComplete ? `${colors.success}15` : colors.surface.secondary }}
          >
            <Text
              style={{ color: isComplete ? colors.success : colors.text.secondary }}
              className="text-xs font-medium"
            >
              {count}/{total}
            </Text>
          </View>
          {isOpen ? (
            <ChevronDown size={18} color={colors.text.tertiary} />
          ) : (
            <ChevronRight size={18} color={colors.text.tertiary} />
          )}
        </View>
      </Pressable>
      {isOpen && <View className="px-4 pb-4">{children}</View>}
    </View>
  );
}

// Document Item Component
function DocumentItem({
  doc,
  status,
  onStatusChange,
  isExpanded,
  onToggle,
  coveringDocName,
  colors,
}: {
  doc: { id: string; name: string; description: string };
  status: DocumentStatus;
  onStatusChange: (status: DocumentStatus) => void;
  isExpanded: boolean;
  onToggle: () => void;
  coveringDocName?: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const statusColors = getStatusColor(status);
  const isCovered = status === 'uploaded' || status === 'verified' || status === 'not-applicable';

  const getStatusIcon = () => {
    switch (status) {
      case 'verified':
        return <CheckCircle2 size={18} color="#16a34a" />;
      case 'uploaded':
        return <CheckCircle2 size={18} color="#2563eb" />;
      case 'in-progress':
        return <Clock size={18} color="#d97706" />;
      case 'not-applicable':
        return <MinusCircle size={18} color="#9ca3af" />;
      default:
        return <Circle size={18} color="#dc2626" />;
    }
  };

  return (
    <View
      className="rounded-lg overflow-hidden mb-2"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: isCovered ? `${colors.success}30` : colors.border.light,
      }}
    >
      <Pressable onPress={onToggle} className="flex-row items-center p-3">
        {getStatusIcon()}
        <View className="flex-1 ml-3">
          <Text
            style={{ color: isCovered ? colors.text.secondary : colors.text.primary }}
            className="text-sm font-medium"
            numberOfLines={1}
          >
            {doc.name}
          </Text>
          {coveringDocName && (
            <Text style={{ color: colors.success }} className="text-xs mt-0.5">
              Covered by: {coveringDocName}
            </Text>
          )}
        </View>
        <View
          className="px-2 py-0.5 rounded"
          style={{ backgroundColor: statusColors.bg }}
        >
          <Text style={{ color: statusColors.text }} className="text-xs font-medium">
            {getStatusLabel(status)}
          </Text>
        </View>
      </Pressable>

      {isExpanded && (
        <View className="px-3 pb-3 pt-1 border-t" style={{ borderColor: colors.border.light }}>
          <Text style={{ color: colors.text.tertiary }} className="text-xs mb-3">
            {doc.description}
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {(['not-started', 'in-progress', 'uploaded', 'verified', 'not-applicable'] as DocumentStatus[]).map((s) => {
              const sColors = getStatusColor(s);
              const isActive = status === s;
              return (
                <Pressable
                  key={s}
                  onPress={() => onStatusChange(s)}
                  className="px-2.5 py-1.5 rounded-md flex-row items-center gap-1.5"
                  style={{
                    backgroundColor: isActive ? sColors.bg : colors.surface.secondary,
                    borderWidth: 1,
                    borderColor: isActive ? sColors.border : 'transparent',
                  }}
                >
                  {isActive && <Check size={12} color={sColors.text} />}
                  <Text
                    style={{ color: isActive ? sColors.text : colors.text.tertiary }}
                    className="text-xs font-medium"
                  >
                    {getStatusLabel(s)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
}

const BREADCRUMBS = [
  { label: 'Dashboard', href: '/(startup)/startup-dashboard' },
  { label: 'Data Room', href: '/(startup)/dataroom' },
  { label: 'Checklist' },
];

export default function DataRoomChecklistScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();

  // Store
  const uploads = useDataRoomStore((s) => s.uploads);
  const currentStage = useDataRoomStore((s) => s.currentStage);
  const domain = useDataRoomStore((s) => s.domain);
  const setCurrentStage = useDataRoomStore((s) => s.setCurrentStage);
  const setDomain = useDataRoomStore((s) => s.setDomain);
  const setDocumentStatus = useDataRoomStore((s) => s.setDocumentStatus);
  const markAsNotApplicable = useDataRoomStore((s) => s.markAsNotApplicable);
  const getCoveringDocument = useDataRoomStore((s) => s.getCoveringDocument);

  // Startup profile for data room connection status
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const isDataRoomConnected = startupProfile?.dataRoomConnected ?? false;
  const dataRoomProvider = startupProfile?.dataRoomProvider;

  // Alert
  const { alert } = useAlert();

  // Local state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedRequired, setExpandedRequired] = useState<Set<string>>(new Set(['corporate-legal']));
  const [expandedOptional, setExpandedOptional] = useState<Set<string>>(new Set());
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [showStageSelector, setShowStageSelector] = useState(false);
  const [showDomainSelector, setShowDomainSelector] = useState(false);

  // Computed - organized by category with required/optional split
  const categoryData = useMemo(() => {
    const categories = Object.keys(CATEGORY_LABELS) as DocumentCategory[];

    return categories.map((category) => {
      const allDocs = DATA_ROOM_DOCUMENTS.filter(
        (doc) => doc.category === category && doc.requirements[currentStage] !== 'none'
      );

      const requiredDocs = allDocs.filter((doc) => isDocumentRequired(doc.requirements[currentStage]));
      const optionalDocs = allDocs.filter((doc) => !isDocumentRequired(doc.requirements[currentStage]));

      const getCompletedCount = (docs: typeof allDocs) =>
        docs.filter((doc) => {
          const status = uploads[doc.id]?.status || 'not-started';
          return status === 'uploaded' || status === 'verified' || status === 'not-applicable';
        }).length;

      return {
        category,
        label: CATEGORY_LABELS[category],
        required: {
          docs: requiredDocs,
          completed: getCompletedCount(requiredDocs),
          total: requiredDocs.length,
        },
        optional: {
          docs: optionalDocs,
          completed: getCompletedCount(optionalDocs),
          total: optionalDocs.length,
        },
        totalCompleted: getCompletedCount(allDocs),
        totalDocs: allDocs.length,
      };
    }).filter((cat) => cat.totalDocs > 0);
  }, [uploads, currentStage]);

  // Domain-specific documents
  const domainDocs = useMemo(() => {
    const docs = getDomainDocuments(domain);
    const domainUploads = useDataRoomStore.getState().domainUploads;

    const requiredDocs = docs.filter((d) => d.required);
    const optionalDocs = docs.filter((d) => !d.required);

    const getCompletedCount = (docList: typeof docs) =>
      docList.filter((doc) => {
        const status = domainUploads[doc.id]?.status || 'not-started';
        return status === 'uploaded' || status === 'verified' || status === 'not-applicable';
      }).length;

    return {
      required: { docs: requiredDocs, completed: getCompletedCount(requiredDocs), total: requiredDocs.length },
      optional: { docs: optionalDocs, completed: getCompletedCount(optionalDocs), total: optionalDocs.length },
    };
  }, [domain]);

  // Progress
  const progress = useMemo(() => calculateProgress(uploads, currentStage), [uploads, currentStage]);

  // Missing documents
  const missingRequired = useMemo(() => {
    return DATA_ROOM_DOCUMENTS.filter((doc) => {
      const isRequired = isDocumentRequired(doc.requirements[currentStage]);
      const status = uploads[doc.id]?.status || 'not-started';
      const isMissing = status !== 'uploaded' && status !== 'verified' && status !== 'not-applicable';
      return isRequired && isMissing;
    });
  }, [uploads, currentStage]);

  const handleStatusChange = (docId: string, newStatus: DocumentStatus) => {
    if (newStatus === 'not-applicable') {
      markAsNotApplicable(docId);
    } else {
      setDocumentStatus(docId, newStatus);
    }
  };

  // Handle upload - behavior depends on data room connection
  const handleUpload = () => {
    if (isDataRoomConnected && dataRoomProvider) {
      const providerName =
        dataRoomProvider === 'google'
          ? 'Google Drive'
          : dataRoomProvider === 'dropbox'
            ? 'Dropbox'
            : 'DocSend';
      alert(
        `Upload to ${providerName}`,
        `This will upload your document directly to your connected ${providerName} data room. Only pitch decks are stored locally for quick sharing.`
      );
    } else {
      alert(
        'Connect Data Room First',
        'Connect your data room (Google Drive, Dropbox, or DocSend) to upload documents. Only pitch decks can be uploaded without a connected data room.'
      );
    }
  };

  // Handle pitch deck upload (always allowed)
  const handlePitchDeckUpload = () => {
    alert(
      'Upload Pitch Deck',
      'Pitch decks are stored locally for quick investor sharing. In a full app, this would open a file picker.'
    );
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleRequired = (category: string) => {
    setExpandedRequired((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleOptional = (category: string) => {
    setExpandedOptional((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  return (
    <AppLayout title="Data Room Checklist" breadcrumbs={BREADCRUMBS}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Mobile-only back header */}
        {phoneShell && (
          <View
            className="flex-row items-center px-4 py-3"
            style={{
              backgroundColor: colors.surface.primary,
              borderBottomWidth: 1,
              borderBottomColor: colors.border.light,
            }}
          >
            <Pressable
              onPress={() => router.push('/(startup)/dataroom')}
              className="flex-row items-center gap-1 mr-3"
              accessibilityLabel="Back to data room"
            >
              <ChevronLeft size={20} color={colors.primary[600]} />
              <Text style={{ color: colors.primary[600] }} className="text-sm font-medium" numberOfLines={1}>
                Data Room
              </Text>
            </Pressable>
            <Text style={{ color: colors.text.primary }} className="text-base font-semibold flex-1" numberOfLines={1}>
              DD Checklist
            </Text>
          </View>
        )}
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 100 }}
        >
        {/* Header */}
        <Animated.View
          entering={FadeIn.duration(400)}
          className="px-5 pt-4 pb-4"
          style={{ backgroundColor: colors.surface.primary }}
        >
          {/* Stage & Domain — inline filter chips */}
          <View className="flex-row gap-2 mb-3">
            <AnimatedPressable
              onPress={() => setShowStageSelector(!showStageSelector)}
              style={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 999,
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: showStageSelector ? colors.primary[400] : colors.border.light,
              }}
            >
              <Text style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '600' }}>
                Stage
              </Text>
              <Text
                style={{ color: colors.text.primary, fontSize: 12, fontWeight: '700', flex: 1 }}
                numberOfLines={1}
              >
                {STAGE_LABELS[currentStage]}
              </Text>
              <ChevronDown
                size={14}
                color={colors.text.tertiary}
                style={{ transform: [{ rotate: showStageSelector ? '180deg' : '0deg' }] }}
              />
            </AnimatedPressable>
            <AnimatedPressable
              onPress={() => setShowDomainSelector(!showDomainSelector)}
              style={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 999,
                backgroundColor: colors.surface.secondary,
                borderWidth: 1,
                borderColor: showDomainSelector ? colors.primary[400] : colors.border.light,
              }}
            >
              <Text style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '600' }}>
                Domain
              </Text>
              <Text
                style={{ color: colors.text.primary, fontSize: 12, fontWeight: '700', flex: 1 }}
                numberOfLines={1}
              >
                {DOMAIN_LABELS[domain]}
              </Text>
              <ChevronDown
                size={14}
                color={colors.text.tertiary}
                style={{ transform: [{ rotate: showDomainSelector ? '180deg' : '0deg' }] }}
              />
            </AnimatedPressable>
          </View>

          {/* Stage Selector Dropdown */}
          {showStageSelector && (
            <View className="mb-3 p-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
              {STAGE_OPTIONS.map((stage) => (
                <Pressable
                  key={stage}
                  onPress={() => {
                    setCurrentStage(stage);
                    setShowStageSelector(false);
                  }}
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: currentStage === stage ? colors.primary[50] : 'transparent' }}
                >
                  <Text
                    style={{ color: currentStage === stage ? colors.primary[600] : colors.text.secondary }}
                    className="font-medium"
                  >
                    {STAGE_LABELS[stage]}
                  </Text>
                </Pressable>
              ))}
            </View>
          )}

          {/* Domain Selector Dropdown */}
          {showDomainSelector && (
            <View className="mb-3 p-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
              <ScrollView style={{ maxHeight: 200 }}>
                {DOMAIN_OPTIONS.map((d) => (
                  <Pressable
                    key={d}
                    onPress={() => {
                      setDomain(d);
                      setShowDomainSelector(false);
                    }}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: domain === d ? colors.primary[50] : 'transparent' }}
                  >
                    <Text
                      style={{ color: domain === d ? colors.primary[600] : colors.text.secondary }}
                      className="font-medium"
                    >
                      {DOMAIN_LABELS[d]}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          )}

          {/* Progress Summary */}
          <View className="flex-row items-center justify-between">
            <View>
              <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
                {progress.completionPercentage}%
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-sm">
                Data Room Complete
              </Text>
            </View>
            <View className="items-end">
              <View className="flex-row items-center gap-1">
                <Shield size={16} color={colors.primary[600]} />
                <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                  {progress.confidenceMultiplier.toFixed(2)}x
                </Text>
              </View>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                Confidence Score
              </Text>
            </View>
          </View>

          {/* Progress Bar */}
          <View className="h-2 rounded-full overflow-hidden mt-3" style={{ backgroundColor: colors.surface.secondary }}>
            <View
              className="h-full rounded-full"
              style={{
                backgroundColor: progress.completionPercentage >= 80 ? colors.success : progress.completionPercentage >= 50 ? colors.warning : colors.error,
                width: `${progress.completionPercentage}%`,
              }}
            />
          </View>
        </Animated.View>

        {/* Missing Alert — surfaced first since it's the most urgent action */}
        {missingRequired.length > 0 && (
          <Animated.View entering={FadeInDown.delay(50).duration(400)} className="mx-5 mt-4">
            <View
              className="p-4 rounded-xl flex-row items-center gap-3"
              style={{ backgroundColor: colors.red.light }}
            >
              <AlertTriangle size={24} color={colors.error} />
              <View className="flex-1">
                <Text style={{ color: colors.error }} className="font-semibold" numberOfLines={1}>
                  {missingRequired.length} Required Documents Missing
                </Text>
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-xs mt-0.5"
                  numberOfLines={2}
                >
                  Complete these to improve investor confidence
                </Text>
              </View>
            </View>
          </Animated.View>
        )}

        {/* Upload Button */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(400)}
          className="px-5 mt-4"
        >
          <AnimatedPressable
            onPress={handleUpload}
            className="rounded-2xl"
            style={{
              backgroundColor: isDataRoomConnected ? colors.primary[600] : colors.surface.primary,
              borderWidth: isDataRoomConnected ? 0 : 1,
              borderColor: colors.border.light,
              shadowColor: isDataRoomConnected ? colors.primary[600] : '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: isDataRoomConnected ? 0.2 : 0.05,
              shadowRadius: 12,
              elevation: 4,
            }}
          >
            <View className="p-4 flex-row items-center justify-between">
              <View className="flex-row items-center gap-3 flex-1 min-w-0">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: isDataRoomConnected ? 'rgba(255,255,255,0.2)' : `${colors.primary[600]}15` }}
                >
                  <Upload size={20} color={isDataRoomConnected ? 'white' : colors.primary[600]} />
                </View>
                <View className="flex-1 min-w-0">
                  <Text
                    style={{ color: isDataRoomConnected ? 'white' : colors.text.primary }}
                    className="font-semibold"
                    numberOfLines={1}
                  >
                    {isDataRoomConnected
                      ? `Upload to ${dataRoomProvider === 'google' ? 'Google Drive' : dataRoomProvider === 'dropbox' ? 'Dropbox' : 'DocSend'}`
                      : 'Upload New Document'}
                  </Text>
                  <Text
                    style={{ color: isDataRoomConnected ? 'rgba(255,255,255,0.8)' : colors.text.tertiary }}
                    className="text-xs"
                    numberOfLines={1}
                  >
                    {isDataRoomConnected
                      ? 'Documents sync to your connected data room'
                      : 'Connect data room or upload pitch deck only'}
                  </Text>
                </View>
              </View>
              <ChevronRight size={20} color={isDataRoomConnected ? 'white' : colors.text.tertiary} />
            </View>
          </AnimatedPressable>
        </Animated.View>

        {/* Section Divider - Documents by Category */}
        <View className="px-5 mt-4">
          <SectionDivider label="Documents by Category" marginTop={0} marginBottom={12} />
        </View>

        {/* Categories */}
        <Animated.View entering={FadeInDown.delay(150).duration(400)} className="px-5">

          {categoryData.map((cat) => (
            <CollapsibleSection
              key={cat.category}
              title={cat.label}
              count={cat.totalCompleted}
              total={cat.totalDocs}
              isOpen={expandedCategories.has(cat.category)}
              onToggle={() => toggleCategory(cat.category)}
              variant="category"
              colors={colors}
            >
              {/* Required Documents */}
              {cat.required.total > 0 && (
                <CollapsibleSection
                  title="Required"
                  count={cat.required.completed}
                  total={cat.required.total}
                  isOpen={expandedRequired.has(cat.category)}
                  onToggle={() => toggleRequired(cat.category)}
                  variant="required"
                  colors={colors}
                >
                  {cat.required.docs.map((doc) => {
                    const status = uploads[doc.id]?.status || 'not-started';
                    const coveringDoc = getCoveringDocument(doc.id);
                    return (
                      <DocumentItem
                        key={doc.id}
                        doc={doc}
                        status={status}
                        onStatusChange={(s) => handleStatusChange(doc.id, s)}
                        isExpanded={expandedDoc === doc.id}
                        onToggle={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}
                        coveringDocName={coveringDoc?.fileName}
                        colors={colors}
                      />
                    );
                  })}
                </CollapsibleSection>
              )}

              {/* Optional Documents */}
              {cat.optional.total > 0 && (
                <CollapsibleSection
                  title="Optional / Recommended"
                  count={cat.optional.completed}
                  total={cat.optional.total}
                  isOpen={expandedOptional.has(cat.category)}
                  onToggle={() => toggleOptional(cat.category)}
                  variant="optional"
                  colors={colors}
                >
                  {cat.optional.docs.map((doc) => {
                    const status = uploads[doc.id]?.status || 'not-started';
                    const coveringDoc = getCoveringDocument(doc.id);
                    return (
                      <DocumentItem
                        key={doc.id}
                        doc={doc}
                        status={status}
                        onStatusChange={(s) => handleStatusChange(doc.id, s)}
                        isExpanded={expandedDoc === doc.id}
                        onToggle={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}
                        coveringDocName={coveringDoc?.fileName}
                        colors={colors}
                      />
                    );
                  })}
                </CollapsibleSection>
              )}
            </CollapsibleSection>
          ))}
        </Animated.View>

        {/* Domain-Specific Documents */}
        {(domainDocs.required.total > 0 || domainDocs.optional.total > 0) && (
          <Animated.View entering={FadeInDown.delay(200).duration(400)} className="px-5 mt-6">
            <View className="flex-row items-center gap-2 mb-3">
              <Sparkles size={16} color={colors.secondary[500]} />
              <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold">
                {DOMAIN_LABELS[domain]} Specific
              </Text>
            </View>

            <View
              className="rounded-xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.secondary[200] }}
            >
              <View className="p-4">
                <Text style={{ color: colors.text.primary }} className="font-semibold mb-1">
                  Industry Requirements
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Additional documents required for {DOMAIN_LABELS[domain]} companies
                </Text>
              </View>

              <View className="px-4 pb-4">
                {domainDocs.required.total > 0 && (
                  <View className="mb-3">
                    <Text style={{ color: colors.error }} className="text-xs font-semibold mb-2 uppercase">
                      Required ({domainDocs.required.completed}/{domainDocs.required.total})
                    </Text>
                    {domainDocs.required.docs.map((doc) => {
                      const status = useDataRoomStore.getState().domainUploads[doc.id]?.status || 'not-started';
                      return (
                        <DocumentItem
                          key={doc.id}
                          doc={doc}
                          status={status}
                          onStatusChange={(s) => useDataRoomStore.getState().setDomainDocumentStatus(doc.id, s)}
                          isExpanded={expandedDoc === `domain-${doc.id}`}
                          onToggle={() => setExpandedDoc(expandedDoc === `domain-${doc.id}` ? null : `domain-${doc.id}`)}
                          colors={colors}
                        />
                      );
                    })}
                  </View>
                )}

                {domainDocs.optional.total > 0 && (
                  <View>
                    <Text style={{ color: colors.info }} className="text-xs font-semibold mb-2 uppercase">
                      Recommended ({domainDocs.optional.completed}/{domainDocs.optional.total})
                    </Text>
                    {domainDocs.optional.docs.map((doc) => {
                      const status = useDataRoomStore.getState().domainUploads[doc.id]?.status || 'not-started';
                      return (
                        <DocumentItem
                          key={doc.id}
                          doc={doc}
                          status={status}
                          onStatusChange={(s) => useDataRoomStore.getState().setDomainDocumentStatus(doc.id, s)}
                          isExpanded={expandedDoc === `domain-${doc.id}`}
                          onToggle={() => setExpandedDoc(expandedDoc === `domain-${doc.id}` ? null : `domain-${doc.id}`)}
                          colors={colors}
                        />
                      );
                    })}
                  </View>
                )}
              </View>
            </View>
          </Animated.View>
        )}
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}
